import { ref } from 'vue';
import { Address, createWalletClient, custom, parseUnits } from 'viem';
import { swapRouterAbi, swapRouterMultiAbi, erc20Abi } from '../lib/abi';
import { useTokens } from './tokens';
import { useWalletStore } from '../stores/wallet';
import { ProviderController } from '@reown/appkit-controllers';
import { useChainStore } from '../stores/chain';

export function useSwap() {
  const wallet = useWalletStore();
  const { selectedFrom, selectedTo } = useTokens();
  const loading = ref(false);
  const error = ref<string | null>(null);
  const txHash = ref<string | null>(null);

  async function approve(amountStr: string) {
    error.value = null;
    txHash.value = null;
    const chain = useChainStore();
    const router = chain.current.uniswap?.router as Address | undefined;
    if (!router) { error.value = 'Router address not configured'; return; }
    if (!selectedFrom.value) { error.value = 'Select From token'; return; }
    if (!wallet.isConnected) { error.value = 'Connect wallet'; return; }
    await wallet.ensureChain(chain.current.chain);
    const amount = parseUnits(amountStr, selectedFrom.value.decimals);
    try {
      loading.value = true;
      const provider = ProviderController.getProvider('eip155' as any) as any;
      const client = createWalletClient({ chain: chain.current.chain, transport: custom(provider || (window as any).ethereum) });
      const hash = await client.writeContract({ account: wallet.address as any, address: selectedFrom.value.address, abi: erc20Abi, functionName: 'approve', args: [router, amount] });
      txHash.value = hash;
      return hash;
    } catch (e: any) {
      error.value = e?.shortMessage || e?.message || 'Approve failed';
    } finally {
      loading.value = false;
    }
  }

  async function swapExactInputSingle(amountInStr: string, minOutStr: string, feeOverride?: number, routePath?: `0x${string}` | null) {
    error.value = null;
    txHash.value = null;
    const chain = useChainStore();
    const router = chain.current.uniswap?.router as Address | undefined;
    const fee = Number((feeOverride ?? chain.current.uniswap?.poolFee) || 3000);
    if (!router) { error.value = 'Router address not configured'; return; }
    if (!selectedFrom.value || !selectedTo.value || !wallet.address) { error.value = 'Missing inputs'; return; }
    await wallet.ensureChain(chain.current.chain);
    const amountIn = parseUnits(amountInStr, selectedFrom.value.decimals);
    const amountOutMinimum = parseUnits(minOutStr || '0', selectedTo.value.decimals);
    const deadline = BigInt(Math.floor(Date.now() / 1000) + 60 * 10); // 10min
    try {
      loading.value = true;
      const provider = ProviderController.getProvider('eip155' as any) as any;
      const client = createWalletClient({ chain: chain.current.chain, transport: custom(provider || (window as any).ethereum) });
      let hash: `0x${string}`;
      if (routePath) {
        // Multi-hop route using exactInput
        hash = await client.writeContract({
          account: wallet.address as any,
          address: router,
          abi: swapRouterMultiAbi,
          functionName: 'exactInput',
          args: [{ path: routePath, recipient: wallet.address!, deadline, amountIn, amountOutMinimum }]
        });
      } else {
        // Single hop
        hash = await client.writeContract({ account: wallet.address as any, address: router, abi: swapRouterAbi, functionName: 'exactInputSingle', args: [{ tokenIn: selectedFrom.value.address, tokenOut: selectedTo.value.address, fee, recipient: wallet.address!, deadline, amountIn, amountOutMinimum, sqrtPriceLimitX96: 0n }] });
      }
      txHash.value = hash;
      return hash;
    } catch (e: any) {
      // Friendlier message when revert reason is not provided
      const msg = e?.shortMessage || e?.message || '';
      if (/execution reverted/i.test(msg) && /unknown/i.test(msg)) {
        error.value = 'Swap reverted. Likely no liquidity at the selected fee or amount too low.';
      } else {
        error.value = msg || 'Swap failed';
      }
    } finally {
      loading.value = false;
    }
  }

  return { loading, error, txHash, approve, swapExactInputSingle };
}
